/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 파일명 : AgGridColsChooser.tsx
// 설명   : ag-Grid 컬럼 표시/숨김 선택 팝업 (공통 컴포넌트)
// 특징   : - 엔터프라이즈 없이 사용 가능 (ColumnApi 활용)
//          - 좌측 상단 톱니 버튼 클릭 시 팝업 표시/토글
//          - 체크박스로 컬럼 가시성 즉시 반영
// 작성자 : Copilot
// 최종수정일 : 2025-10-15
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AgGridReact } from 'ag-grid-react';
import type { Column } from 'ag-grid-community';
import { useClickOutside } from '../hooks/useClickOutside';
import logo from '../logo.svg';
import '../styles/AgGridColsChooser.css';

export type AnyRow = Record<string, unknown>;
export type GridRef<T extends AnyRow = AnyRow> = React.RefObject<AgGridReact<T>>;

interface AgGridColsChooserProps<T extends AnyRow = AnyRow> {
  gridRef: GridRef<T> | GridRef<T>[];
  title: string;
  groups?: number;
  // 페이징사용 토글 시 즉시 DB에 저장(ZA_USER_GRID) - callPgmId/gridId를 몰라도 되게 부모(AgGrid)가
  // saveGridLayout()을 호출하는 콜백만 넘겨줌(2026-10-07). 없으면 저장은 안 하고 화면에만 반영.
  onSaveLayout?: () => void;
}

function chunkArray<T>(arr: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) out.push(arr.slice(i, i + chunkSize));
  return out;
}

function AgGridColsChooser({ gridRef, title, groups = 3, onSaveLayout }: AgGridColsChooserProps) {
  // 2026-09-27: 매 렌더마다 새 배열([gridRef])을 만들면(gridRef가 단일 ref일 때) refs 참조가 계속 바뀌고,
  // 그걸 의존성으로 삼는 readColumns/refreshFromApi도 매번 새로 만들어지고, 그걸 의존성으로 삼는 아래
  // useEffect가 open===true인 동안 매 렌더마다 다시 실행 -> refreshFromApi() -> setCols() -> 재렌더 ->
  // 다시 refs가 바뀜 -> ... 무한루프(Maximum update depth exceeded)에 빠지던 문제. gridRef(useRef로 만든
  // 안정적인 참조) 자체를 의존성으로 삼아 refs 참조를 고정함.
  const refs = useMemo(() => (Array.isArray(gridRef) ? gridRef : [gridRef]), [gridRef]);
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState<Array<{ colId: string; headerName: string; visible: boolean; locked: boolean }>>([]);
  const mapRef = useRef<Record<string, boolean>>({});
  const wrapperRef = useRef<HTMLDivElement>(null); // 바깥 클릭 감지용(버튼+팝업 전체 포함)

  // 팝업이 열려있을 때, 바깥(다른 곳) 클릭 시 자동으로 닫기
  useClickOutside(wrapperRef, () => setOpen(false), open);

  const readColumns = useCallback(() => {
    // v34: Column API가 api로 통합됨(columnApi는 더 이상 존재하지 않음 - 늘 undefined라 아래 columnApi 참조는 항상 빈 배열 반환하던 버그)
    const gridApi = (refs[0].current as any)?.api;
    if (!gridApi) return [] as Column[];
    const list: Column[] =
      (gridApi.getColumns?.() as Column[] | undefined)
      || (gridApi.getAllGridColumns?.() as Column[] | undefined)
      || [];
    return list;
  }, [refs]);

  // 페이징사용 - 컬럼이 아니라 그리드 전체 옵션(pagination)이라 컬럼 목록과는 별도로 관리함.
  // 끄고 켜면 즉시 그리드에 반영되고, "그리드위치저장"(saveGridLayout)이 저장 시점에
  // gridApi.getGridOption('pagination')로 현재 상태를 그대로 읽어가므로 별도 저장 로직은 필요 없음(2026-10-07).
  const [paginationOn, setPaginationOn] = useState(true);

  const refreshFromApi = useCallback(() => {
    const gridApiForPaging = (refs[0].current as any)?.api;
    const pag = gridApiForPaging?.getGridOption?.('pagination');
    setPaginationOn(pag !== false);
    const columns = readColumns();
    const next = columns.map(col => {
      const def = col.getColDef?.() as any;
      const headerName = def?.headerName || col.getColId?.() || '';
      const colId = col.getColId?.() || headerName;
      const visible = typeof col.isVisible === 'function' ? !!col.isVisible() : !def?.hide;
      // 잠금 대상 컬럼(목록엔 보이되 체크박스는 선택 못하게 처리):
      //  - No/CUD처럼 항상 고정인 컬럼(colDef.lockPinned)
      //  - 소스코드에 hide: true로 박아둔 컬럼(DB 저장용 필수값 등 - 사용자가 숨김/보이기를 건드리면 위험한 컬럼).
      //    getColDef()는 최초 정의된 colDef 원본을 그대로 반환하므로, 사용자가 나중에 컬럼선택기로 보이기/숨기기를
      //    토글해도 이 값은 안 바뀜 - 즉 "코드가 처음부터 숨긴 것"과 "사용자가 나중에 숨긴 것"을 구분할 수 있음.
      //    사용자가 직접 숨긴(원래 hide가 없던) 컬럼은 여기 해당 안 되어 계속 자유롭게 토글 가능.
      const locked = !!def?.lockPinned || !!def?.hide;
      return { colId, headerName, visible, locked };
    });
    // 상태 보관 맵 갱신
    mapRef.current = next.reduce((acc, c) => { acc[c.colId] = c.visible; return acc; }, {} as Record<string, boolean>);
    setCols(next);
  }, [readColumns, refs]);

  // open 시점이나 gridRef가 바뀔 때 컬럼 목록/가시성/페이징사용 갱신
  useEffect(() => {
    if (open) refreshFromApi();
  }, [open, refs, refreshFromApi]);

  const onTogglePagination = useCallback((checked: boolean) => {
    const gridApi = (refs[0].current as any)?.api;
    if (!gridApi) return;
    gridApi.updateGridOptions?.({ pagination: checked });
    setPaginationOn(checked);
    onSaveLayout?.(); // 컬럼 체크박스(onToggle)와 달리 이 토글만 클릭 즉시 DB에 저장함
  }, [refs, onSaveLayout]);

  const onToggle = useCallback((colId: string, checked: boolean) => {
    if (cols.find(c => c.colId === colId)?.locked) return; // 고정 컬럼은 체크박스가 disabled라 여기 안 타지만 안전장치로 한 번 더 막음
    const gridApi = (refs[0].current as any)?.api;
    if (!gridApi) return;
    // v34: setColumnVisible(단수)는 없어지고 setColumnsVisible(배열)로 통합됨
    gridApi.setColumnsVisible?.([colId], checked);
    mapRef.current[colId] = checked;
    setCols(prev => prev.map(c => (c.colId === colId ? { ...c, visible: checked } : c)));
  }, [refs, cols]);

  // 열 배치 계산: groups가 우선, 같으면 columnsPerGroup으로 분할
  const grouped = useMemo(() => {
    if (!cols.length) return [] as Array<Array<typeof cols[number]>>;
    if (groups > 0) {
      const size = Math.ceil(cols.length / groups);
      return chunkArray(cols, size);
    }
    // 기본 3그룹
    const size = Math.ceil(cols.length / 3);
    return chunkArray(cols, size);
  }, [cols, groups]);

  // ESC로 닫기 (원하면 제거 가능)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="column-chooser-wrapper" ref={wrapperRef}>
      <button type="button" className="corner-chooser-button" aria-label={title} onClick={() => setOpen(v => !v)}>
        <img src={logo} alt="" className="corner-chooser-icon" />
      </button>
      {open && (
        <div className="column-chooser-popup" role="dialog" aria-label={title}>
          <div className="column-chooser-header">
            <span className="column-chooser-title">{title}</span>
          </div>
          <div className="column-chooser-body">
            {grouped.map((group, idx) => (
              <div className="column-chooser-col" key={idx}>
                {group.map(item => (
                  <label
                    className={`column-chooser-item${item.locked ? ' locked' : ''}`}
                    key={item.colId}
                    title={item.headerName}
                  >
                    <input
                      type="checkbox"
                      checked={!!item.visible}
                      disabled={item.locked}
                      onChange={e => onToggle(item.colId, e.target.checked)}
                    />
                    <span>{item.headerName}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div className="column-chooser-footer">
            <label className="column-chooser-item">
              <input
                type="checkbox"
                checked={paginationOn}
                onChange={e => onTogglePagination(e.target.checked)}
              />
              <span>페이징사용</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgGridColsChooser;
